import { ServiceLevelFeature, ServiceLevelMarketingInfo } from 'types/service-level';

export const serviceLevelsMarketing: Array<ServiceLevelMarketingInfo> = [
	{
		name: 'STARTER',
		label: 'Starter',
		subtitle: 'Get started with a basic portfolio that you can build upon.',
		description:
			'With our lowest minimum initial investment, Starter offers a convenient way to evaluate whether Fundrise — and real estate investing — is a good fit. When you’re ready, invest a total of $1,000 to upgrade to a Basic account. If you don’t love Fundrise, request a redemption anytime within your first 90 days, and we’ll buy your investment back at the original amount*.',
		keyFeatures: [
			{
				label: 'Become a real estate investor',
				icon: 'checkmark'
			},
			{
				label: 'Start earning potential returns',
				icon: 'checkmark'
			},
			{
				label: 'Free upgrade to a Core account when you’re ready',
				icon: 'checkmark'
			}
		],
		minInvestment: 10,
		theme: 'light',
		ctaLabel: 'Start here',
		investmentProductAvailability: [
			{
				name: 'Core plans',
				available: false
			},
			{
				name: 'Plus plans',
				available: false
			},
			{
				name: 'Private funds',
				available: false
			}
		],
		demographicProfile: [
			'Earlier on in their career',
			'New to investing outside a 401k or IRA',
			'Looking for a simple, hands-off way to begin investing in real estate'
		],
		simplifiedBenefitsText:
			'Just getting started? With Starter, you can invest in our diversified flagship fund and enjoy our simplest experience and lowest minimum.',
		updatedSubtitle: 'Start with our simplest experience and lowest minimum.',
		iconName: 'service-level-starter-32x32',
		featuredImageUrl: '/images/marketing-site/Investments-ExploreCorePlans3.png',
		urlSlug: 'starter',
		disclaimers:
			'*Redemption requests are typically processed at the end of each quarter. Real estate is an inherently illiquid investment and Fundrise may limit or pause redemptions during a financial crisis or other period of instability.'
	},
	{
		name: 'BASIC',
		label: 'Basic',
		subtitle: 'Get started with a simple real estate portfolio.',
		description: 'Start your real estate investing journey with a basic portfolio of private real estate assets.',
		keyFeatures: [
			{
				label: 'Dividend reinvestment + auto-invest',
				icon: 'checkmark'
			},
			{
				label: 'Create and manage investment goals',
				icon: 'checkmark'
			},
			{
				label: '3 months of advisory fees waived for each friend you invite',
				icon: 'checkmark'
			}
		],
		minInvestment: 1000,
		theme: 'light',
		ctaLabel: 'Start here',
		investmentProductAvailability: [
			{
				name: 'Core plans',
				available: false
			},
			{
				name: 'Plus plans',
				available: false
			},
			{
				name: 'Private funds',
				available: false
			}
		],
		demographicProfile: [
			'Earlier on in their career',
			'New to investing outside a 401k or IRA',
			'Interested in investing toward a personal financial objective'
		],
		simplifiedBenefitsText:
			'Basic unlocks all the essentials, including investment goals, IRA investing, and the potential of investing in the Fundrise iPO. ',
		updatedSubtitle: 'All of the essentials, including IRA investing and more.',
		iconName: 'service-level-basic-32x32',
		featuredImageUrl: '/images/marketing-site/Investments-BasicPlan.png',
		urlSlug: 'basic'
	},
	{
		name: 'CORE',
		label: 'Core',
		subtitle: 'Grow a well-rounded portfolio that&rsquo;s aligned with your goals.',
		description:
			'Build on the foundation of your investing experience with core plans, and tailor your Fundrise portfolio to your goals. Select a strategy focused on generating income, maximizing long-term growth, or achieving a balance of both.',
		keyFeatures: [
			{
				label: 'Includes all Basic features, plus...',
				icon: 'checkmark'
			},
			{
				label: 'Exposure to private eREIT funds',
				icon: 'plus-sign'
			},
			{
				label: 'Ability to customize your investing strategy',
				icon: 'plus-sign'
			}
		],
		minInvestment: 5000,
		theme: 'light',
		ctaLabel: 'Start here',
		investmentProductAvailability: [
			{
				name: 'Core plans',
				available: true
			},
			{
				name: 'Plus plans',
				available: false
			},
			{
				name: 'Private funds',
				available: false
			}
		],
		demographicProfile: [
			'Working professionals with established careers',
			'Have some experience with investing outside of a retirement account',
			'Looking for a diversified, hands-off investment with simple tax reporting'
		],
		simplifiedBenefitsText:
			'Core allows you to customize your Fundrise portfolio. Here you can choose your preferred return profile and even make direct investments into specific funds.',
		updatedSubtitle: 'Take control of your portfolio with increased customization.',
		iconName: 'service-level-core-32x32',
		featuredImageUrl: '/images/marketing-site/Investments-ExploreCorePlans3.png',
		urlSlug: 'core'
	},
	{
		name: 'ADVANCED',
		label: 'Advanced',
		subtitle: 'Access more advanced strategies with higher potential returns.',
		description:
			'Advanced investors gain access to Plus plans, an optional add-on to each Core plan. These plans typically allocate a portion of your portfolio to more sophisticated real estate strategies that evolve over time based on new market opportunities.',
		keyFeatures: [
			{
				label: 'Includes all Core features, plus...',
				icon: 'checkmark'
			},
			{
				label: 'Ability to allocate directly to most of our funds',
				icon: 'plus-sign'
			},
			{
				label: '9 months of advisory fees waived for each friend you invite',
				icon: 'plus-sign'
			}
		],
		minInvestment: 10000,
		theme: 'light',
		ctaLabel: 'Start here',
		investmentProductAvailability: [
			{
				name: 'Core plans',
				available: true
			},
			{
				name: 'Plus plans',
				available: true
			},
			{
				name: 'Private funds',
				available: false
			}
		],
		demographicProfile: [
			'Working professionals with established careers',
			'Have some investing experience',
			'Comfortable with more complex tax filing requirements such as K-1s'
		],
		simplifiedBenefitsText:
			"If you're interested in expanding beyond our broadest offerings, Advanced investors have access to our more sophisticated investment products.",
		updatedSubtitle: 'Access sophisticated strategies with higher return potential.',
		iconName: 'service-level-advanced-32x32',
		featuredImageUrl: '/images/marketing-site/Investments-SingleFamilyStrategy.png',
		urlSlug: 'advanced'
	},
	{
		name: 'PREMIUM',
		label: 'Premium',
		subtitle: 'One-of-a-kind investments paired with unparalleled service.',
		description:
			'Premium investors can access private funds that periodically become available. These offerings are usually specialized, truly illiquid private equity funds with even longer time horizons — but carry the potential for outsized performance.',
		keyFeatures: [
			{
				label: 'Includes all Advanced features, plus...',
				icon: 'checkmark'
			},
			{
				label: 'Priority access to our Investor Relations team',
				icon: 'plus-sign'
			},
			{
				label: '12 months of advisory fees waived for each friend you invite',
				icon: 'plus-sign'
			}
		],
		minInvestment: 100000,
		theme: 'dark',
		ctaLabel: 'Start here',
		investmentProductAvailability: [
			{
				name: 'Core plans',
				available: true
			},
			{
				name: 'Plus plans',
				available: true
			},
			{
				name: 'Private funds',
				available: true
			}
		],
		demographicProfile: [
			'High net worth or accredited investors',
			'Have real estate investing experience',
			'Comfortable with capital calls or similar structured funding mechanisms'
		],
		simplifiedBenefitsText:
			'Premium is our highest tier, designed with accredited investors in mind. Enjoy access to periodic private equity funds and priority access to our Investor Relations team.',
		updatedSubtitle: 'One-of-a-kind offerings designed for accredited investors.',
		iconName: 'service-level-premium-32x32',
		featuredImageUrl: '/images/marketing-site/Investments-OpportunityFund.jpg',
		urlSlug: 'premium'
	}
];

export const minInvestmentAmount = serviceLevelsMarketing.filter((level) => level.name === 'STARTER')[0].minInvestment;

export function serviceLevelWaiverPeriod(serviceLevelsMarketing: ServiceLevelMarketingInfo): number {
	const serviceLevelSlug = serviceLevelsMarketing.urlSlug;
	switch (serviceLevelSlug) {
		case 'premium':
			return 365;
		case 'advanced':
			return 270;
		case 'core':
			return 180;
		case 'starter':
		case 'basic':
		default:
			return 90;
	}
}

export const serviceLevelFeatures: Array<ServiceLevelFeature> = [
	{
		name: 'accountFeatures',
		label: 'Account Features',
		categories: [
			{
				name: 'drip',
				label: 'Dividend reinvestment',
				explanation: '',
				availability: [
					{
						serviceLevel: 'STARTER',
						available: true
					},
					{
						serviceLevel: 'BASIC',
						available: true
					},
					{
						serviceLevel: 'CORE',
						available: true
					},
					{
						serviceLevel: 'ADVANCED',
						available: true
					},
					{
						serviceLevel: 'PREMIUM',
						available: true
					}
				]
			},
			{
				name: 'autoInvest',
				label: 'Auto-invest',
				explanation: '',
				availability: [
					{
						serviceLevel: 'STARTER',
						available: true
					},
					{
						serviceLevel: 'BASIC',
						available: true
					},
					{
						serviceLevel: 'CORE',
						available: true
					},
					{
						serviceLevel: 'ADVANCED',
						available: true
					},
					{
						serviceLevel: 'PREMIUM',
						available: true
					}
				]
			},
			{
				name: 'goals',
				label: 'Create and manage Investor Goals',
				explanation: '',
				availability: [
					{
						serviceLevel: 'STARTER',
						available: 'Limited',
						limitedTooltip:
							'Access to one Investor Goal type. Basic and above have access to full Investor Goals feature, including Income Goal type.'
					},
					{
						serviceLevel: 'BASIC',
						available: true
					},
					{
						serviceLevel: 'CORE',
						available: true
					},
					{
						serviceLevel: 'ADVANCED',
						available: true
					},
					{
						serviceLevel: 'PREMIUM',
						available: true
					}
				]
			},
			{
				name: 'ira',
				label: 'Invest via IRA',
				explanation: '',
				availability: [
					{
						serviceLevel: 'STARTER',
						available: false
					},
					{
						serviceLevel: 'BASIC',
						available: true
					},
					{
						serviceLevel: 'CORE',
						available: true
					},
					{
						serviceLevel: 'ADVANCED',
						available: true
					},
					{
						serviceLevel: 'PREMIUM',
						available: true
					}
				]
			},
			{
				name: 'customizeStrategy',
				label: 'Customize portfolio strategy',
				explanation:
					'Investors in our Core account level and above are able to customize their portfolio through diversifying across a wider variety of funds with specific objectives, such as income, growth, and balanced.',
				availability: [
					{
						serviceLevel: 'STARTER',
						available: false
					},
					{
						serviceLevel: 'BASIC',
						available: false
					},
					{
						serviceLevel: 'CORE',
						available: true
					},
					{
						serviceLevel: 'ADVANCED',
						available: true
					},
					{
						serviceLevel: 'PREMIUM',
						available: true
					}
				]
			},
			{
				name: 'allocateFunds',
				label: 'Directly allocate to funds',
				explanation: '',
				availability: [
					{
						serviceLevel: 'STARTER',
						available: false
					},
					{
						serviceLevel: 'BASIC',
						available: false
					},
					{
						serviceLevel: 'CORE',
						available: true
					},
					{
						serviceLevel: 'ADVANCED',
						available: true
					},
					{
						serviceLevel: 'PREMIUM',
						available: true
					}
				]
			},
			{
				name: 'priorityAccess',
				label: 'Priority access to Investor Relations team',
				explanation: '',
				availability: [
					{
						serviceLevel: 'STARTER',
						available: false
					},
					{
						serviceLevel: 'BASIC',
						available: false
					},
					{
						serviceLevel: 'CORE',
						available: false
					},
					{
						serviceLevel: 'ADVANCED',
						available: false
					},
					{
						serviceLevel: 'PREMIUM',
						available: true
					}
				]
			}
		]
	},
	{
		name: 'offerings',
		label: 'Offerings',
		categories: [
			{
				name: 'registeredProducts',
				label: 'Registered products',
				explanation:
					'Registered products are the fund(s) that form the foundation of a new Fundrise investor’s portfolio. These fund(s) invest in a diversified portfolio of private real estate.',
				availability: [
					{
						serviceLevel: 'STARTER',
						available: true
					},
					{
						serviceLevel: 'BASIC',
						available: true
					},
					{
						serviceLevel: 'CORE',
						available: true
					},
					{
						serviceLevel: 'ADVANCED',
						available: true
					},
					{
						serviceLevel: 'PREMIUM',
						available: true
					}
				]
			},
			{
				name: 'nonregisterdProducts',
				label: 'Non-registered products',
				explanation:
					'Investors at the Core account level and above also have access to non-registered funds such as eREITs, which are private funds that invest in real estate according to a specific investment objective.',
				availability: [
					{
						serviceLevel: 'STARTER',
						available: false
					},
					{
						serviceLevel: 'BASIC',
						available: false
					},
					{
						serviceLevel: 'CORE',
						available: true
					},
					{
						serviceLevel: 'ADVANCED',
						available: true
					},
					{
						serviceLevel: 'PREMIUM',
						available: true
					}
				]
			},
			{
				name: 'plusPlans',
				label: 'Plus plans',
				explanation:
					'Plus plans are an optional add-on for investors at the Advanced account level. These plans typically allocate a portion of your portfolio to more sophisticated real estate strategies that evolve over time based on new market opportunities.',
				availability: [
					{
						serviceLevel: 'STARTER',
						available: false
					},
					{
						serviceLevel: 'BASIC',
						available: false
					},
					{
						serviceLevel: 'CORE',
						available: false
					},
					{
						serviceLevel: 'ADVANCED',
						available: true
					},
					{
						serviceLevel: 'PREMIUM',
						available: true
					}
				]
			},
			{
				name: 'accreditedOfferings',
				label: 'Periodic accredited offerings',
				explanation:
					'Premium investors can access private funds that periodically become available. These offerings are usually specialized, truly illiquid private equity funds with varying risk, return profiles, and time horizons.',
				availability: [
					{
						serviceLevel: 'STARTER',
						available: false
					},
					{
						serviceLevel: 'BASIC',
						available: false
					},
					{
						serviceLevel: 'CORE',
						available: false
					},
					{
						serviceLevel: 'ADVANCED',
						available: false
					},
					{
						serviceLevel: 'PREMIUM',
						available: true
					}
				]
			},
			{
				name: 'fundriseIpo',
				label: 'Access to Fundrise iPO',
				explanation:
					'The Fundrise iPO gives investors the opportunity to buy an ownership stake in Fundrise, the company, itself. When you invest in the Fundrise iPO, you are purchasing shares of Rise Companies Corp., the parent company and owner of Fundrise.',
				availability: [
					{
						serviceLevel: 'STARTER',
						available: false
					},
					{
						serviceLevel: 'BASIC',
						available: true
					},
					{
						serviceLevel: 'CORE',
						available: true
					},
					{
						serviceLevel: 'ADVANCED',
						available: true
					},
					{
						serviceLevel: 'PREMIUM',
						available: true
					}
				]
			}
		]
	},
	{
		name: 'feesAndWaivers',
		label: 'Fees and Waivers',
		categories: [
			{
				name: 'annualAdvisoryFee',
				label: 'Annual advisory fee',
				explanation: `Investors pay a 0.15% annual investment advisory fee, which may be waived under certain circumstances. This means that over a 12-month period, you will pay a $1.50 advisory fee for every $1,000 you've invested with us. You can find a full description of all fees in our <a href="https://fundrise.com/oc" target="_blank" class="text-color-current-color">offering circulars</a>.`,
				feesInfo: [
					{
						serviceLevel: 'STARTER',
						value: '0.15%'
					},
					{
						serviceLevel: 'BASIC',
						value: '0.15%'
					},
					{
						serviceLevel: 'CORE',
						value: '0.15%'
					},
					{
						serviceLevel: 'ADVANCED',
						value: '0.15%'
					},
					{
						serviceLevel: 'PREMIUM',
						value: '0.15%'
					}
				]
			},
			{
				name: 'annualAssetMgmtFee',
				label: 'Annual asset management fee',
				explanation: `Separately, the funds in our standard portfolios pay a 0.85% annual asset management fee. That's $8.50/year for every $1,000 invested. You can find a full description of all fees in our <a href="https://fundrise.com/oc" target="_blank" class="text-color-current-color">offering circulars</a>.`,
				feesInfo: [
					{
						serviceLevel: 'STARTER',
						value: 'up to 0.85%'
					},
					{
						serviceLevel: 'BASIC',
						value: 'up to 0.85%'
					},
					{
						serviceLevel: 'CORE',
						value: 'up to 0.85%'
					},
					{
						serviceLevel: 'ADVANCED',
						value: 'up to 0.85%'
					},
					{
						serviceLevel: 'PREMIUM',
						value: 'up to 0.85%'
					}
				]
			},
			{
				name: 'advisoryFeeWaiverDays',
				label: 'Days of advisory fee waiver per successful invite',
				explanation: `Investors can waive their advisory fees for a period of time by inviting others to join Fundrise through a unique invite link. <a href="https://fundrise.com/legal/invitation-program" target="_blank" class="text-color-current-color">See full terms and conditions</a>.`,
				feesInfo: [
					{
						serviceLevel: 'STARTER',
						value: `${serviceLevelWaiverPeriod(serviceLevelsMarketing[0])} days`
					},
					{
						serviceLevel: 'BASIC',
						value: `${serviceLevelWaiverPeriod(serviceLevelsMarketing[1])} days`
					},
					{
						serviceLevel: 'CORE',
						value: `${serviceLevelWaiverPeriod(serviceLevelsMarketing[2])} days`
					},
					{
						serviceLevel: 'ADVANCED',
						value: `${serviceLevelWaiverPeriod(serviceLevelsMarketing[3])} days`
					},
					{
						serviceLevel: 'PREMIUM',
						value: `${serviceLevelWaiverPeriod(serviceLevelsMarketing[4])} days`
					}
				]
			}
		]
	}
];
